import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Keep a conversation scrolled to its newest message.
 *
 * Scrolling when the message count changed was not enough: a reply lands, the
 * view scrolls, and then its chart, its product pictures and its proposal card
 * render and push the answer back below the fold. So this watches the
 * content's size instead, and follows it down whenever it grows — as long as
 * the vendor was already at the bottom. Someone who scrolled up to reread an
 * earlier answer is left where they are; sending a message always brings them
 * down, because that is where their question and its answer are.
 */

/** How close to the bottom still counts as "at the bottom", in pixels. */
export const NEAR_BOTTOM = 120;

/** Whether a scroller is showing its end, give or take. Pure, for the tests. */
export function isNearBottom(el: { scrollHeight: number; scrollTop: number; clientHeight: number }, slack = NEAR_BOTTOM): boolean {
    return el.scrollHeight - el.scrollTop - el.clientHeight <= slack;
}

export function useStickToBottom(
    scrollerRef: RefObject<HTMLElement>,
    contentRef: RefObject<HTMLElement>,
    /** Changes whenever the vendor sends or opens a conversation — the moment to go down regardless. */
    sentKey: unknown,
): void {
    // Whether new content should be followed. Starts true: a conversation opens at its end.
    const pinned = useRef(true);

    /*
     * The elements, read at render. The panel mounts its scroller only once it
     * is open, so effects keyed on the ref object alone would attach to
     * nothing and never try again; keyed on the element, they re-attach the
     * render after it appears.
     */
    const scroller = scrollerRef.current;
    const content = contentRef.current;

    // Sending always goes down — before paint, so their message is never off-screen.
    useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        pinned.current = true;
        el.scrollTop = el.scrollHeight;
    }, [scrollerRef, sentKey, scroller]);

    // Track whether the vendor has scrolled away from the end.
    useEffect(() => {
        if (!scroller) return;
        const onScroll = () => {
            pinned.current = isNearBottom(scroller);
        };
        scroller.addEventListener('scroll', onScroll, { passive: true });
        return () => scroller.removeEventListener('scroll', onScroll);
    }, [scroller]);

    // Follow the content as it grows: replies, live steps, charts, images, cards.
    useEffect(() => {
        if (!scroller || !content || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => {
            if (pinned.current) scroller.scrollTop = scroller.scrollHeight;
        });
        observer.observe(content);
        return () => observer.disconnect();
    }, [scroller, content]);
}
